/* eslint-disable */

import { supabase, supabaseWeather } from 'Services/supabaseClient'
import { WEATHER_METHODS } from '../Utils/methods'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'

const {
  renderMethods: { getCountryFlag }
} = WEATHER_METHODS

const fetchUniqueCountriesData = async () => {
  const { data, error } = await supabaseWeather.rpc('get_countries_for_weather')

  return (data || []).map((item) => ({
    ...item,
    flagURL: getCountryFlag(item.country_code)
  }))
}

const fetchUniqueStatesData = async (countryId) => {
  const { data, error } = await supabaseWeather.rpc('get_states_for_country', { p_country_id: countryId })

  return (data || []).map((item) => ({
    ...item,
    country_id: countryId,
    state: item.state_name // alias so UI can keep using "state"
  }))
}

const fetchUniqueRegionsData = async (countryId, stateName) => {
  const { data, error } = await supabaseWeather.rpc('get_regions_for_country_state', { p_country_id: countryId, p_state_name: stateName })

  return (data || []).map((item) => ({
    ...item,
    country_id: countryId,
    state: stateName,
    region: item.region_name // alias so UI can keep using "region"
  }))
}

const fetchWeatherDataByCountry = async (countryId, userSearchLocations, state = null, region = null) => {
  let locations = []

  if (userSearchLocations?.length) {
    // For search, keep existing behavior: use the search result list
    locations = userSearchLocations
  } else if (countryId) {
    // NEW: get ordered locations from RPC instead of querying cww_locations directly
    const rpcData = await fetchCountryStateRegionLocations(countryId, state, region)
    locations = rpcData || []
  }

  if (!locations?.length) return []

  const locationIds = locations.map((loc) => loc?.location_id || loc.id).filter(Boolean)

  let userLocationMap = {}

  const userDetails = getLocalUserDetails()

  if (locationIds.length > 0 && userDetails?.id) {
    const { data: userLocations } = await supabaseWeather.from('cww_user_locations').select('*').eq('user_id', userDetails?.id).in('location_id', locationIds)
    userLocationMap = (userLocations || []).reduce((acc, item) => {
      acc[item.location_id] = true
      return acc
    }, {})
  }

  let locationDetailsMap = {}

  if (locationIds.length > 0) {
    const { data: locationData } = await supabaseWeather
      .from('cww_locations')
      .select(
        `
      id,
      timezone,
      zone,
      country_code,
      country_id,
      country_name,
      latitude,
      longitude,
      region,
      state
    `
      )
      .in('id', locationIds)

    locationDetailsMap = (locationData || []).reduce((acc, loc) => {
      acc[loc.id] = loc
      return acc
    }, {})
  }

  const weatherData = await Promise.all(
    locations.map(async (location) => {
      const locationId = location?.location_id || location.id

      const { data } = await supabaseWeather
        .from('cww_weather_data')
        .select('*')
        .eq('location_id', location?.location_id || location.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single()

      return {
        ...location,
        ...locationDetailsMap[locationId],
        isInUserLocation: !!userLocationMap[locationId],
        weather: data
      }
    })
  )

  return weatherData
}

const fetchFortNightWeatherData = async (locationId) => {
  const fortNightWeatherResponse = await supabaseWeather.rpc('cww_fetch_weather_forecast', { location_id: locationId })

  return fortNightWeatherResponse
}

const fetchHourlyWeatherData = async (locationId, forecastDate) => {
  const hourlyWeatherResponse = await supabaseWeather.rpc('cww_fetch_weather_hourly', { location_id: locationId, forecast_date: forecastDate })

  return hourlyWeatherResponse
}

const fetchCountryStateRegionLocations = async (countryId, stateName, regionName) => {
  const { data } = await supabaseWeather.rpc('get_locations_for_country_state_region', { p_country_id: countryId, p_state_name: stateName, p_region_name: regionName })

  return data
}

const fetchSearchResults = async (query) => {
  const { data, error } = await supabaseWeather.from('cww_locations').select('*').ilike('location_name', `%${query}%`).order('location_name', { ascending: true }).limit(10)

  return { data, error }
}

// Commented for Reference
// const fetchSearchResults = async (query) => {
//   const { data, error } = await supabaseWeather
//     .from('cww_locations')
//     .select('*')
//     .or(`location_name.ilike.%${query}%,region.ilike.%${query}%,country_name.ilike.%${query}%,state.ilike.%${query}%`)
//     .order('location_name', { ascending: true })
//     .limit(20)

//   return { data, error }
// }

const fetchUserSavedLocations = async (userId) => {
  const { data, error } = await supabaseWeather.from('cww_user_locations').select('*').eq('user_id', userId).order('created_at', { ascending: false })

  return { data, error }
}

const saveLocationForUser = async (userId, locationId, locationName, latitude, longitude, countryCode, state, region, countryName, zone, countryId) => {
  await supabaseWeather.from('cww_user_locations').upsert([{ user_id: userId, location_id: locationId, location_name: locationName, latitude, longitude, country_code: countryCode, state, region, country_name: countryName, zone, country_id: countryId }], { onConflict: 'user_id,location_id' })
}

const deleteLocationForUser = async (userId, locationId) => {
  await supabaseWeather.from('cww_user_locations').delete().eq('user_id', userId).eq('location_id', locationId)
}

export { fetchWeatherDataByCountry, fetchSearchResults, fetchUserSavedLocations, saveLocationForUser, deleteLocationForUser, fetchCountryStateRegionLocations, fetchUniqueCountriesData, fetchUniqueStatesData, fetchUniqueRegionsData, fetchFortNightWeatherData, fetchHourlyWeatherData }
